import type { GetProductsQuery } from '@kimuno/api-sdk/lib/services/product';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { fetchProductsByQuery as fetchProductsByQueryAction } from '../../../actions/product.actions';
import constants, {
	CAROUSEL_DEFAULT_QUERY_OPTIONS,
	CAROUSEL_DEFAULT_SUBJECT_ID,
} from '../../../constants';
import { selectUser } from '../../../reducers/user.selectors';
import useAction from '../../../utils/useAction';

/*
 * This hook returns subjects for the Dashboard page
 * and it also updates the Redux store with these products.
 */
// `enabled` guards the legacy product fetches: the profile-aware dashboard
// (PRO-4 treatment) renders its own rows and must not also fire these, to avoid
// duplicate requests. The returned subject lists are always derived (no fetch).
const useFetchDashboardSubjects = (enabled = true) => {
	const user = useSelector(selectUser);
	const fetchProductsByQuery = useAction(fetchProductsByQueryAction);
	const defaultSubject = constants.subjectsById[CAROUSEL_DEFAULT_SUBJECT_ID];
	const userSubjects = [
		...(user?.subjectsFlattened.filter((subject) => subject.id !== CAROUSEL_DEFAULT_SUBJECT_ID) ||
			[]),
	];

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!enabled) return;
		// All users should see didactic product recommendations, regardless of
		// their subject / school type choices. This request is separated from the others
		// so that it is only fired once.
		fetchProductsByQuery({ subjects: [CAROUSEL_DEFAULT_SUBJECT_ID] }, CAROUSEL_DEFAULT_QUERY_OPTIONS);
	}, [enabled]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!enabled) return;
		if (!user) return;

		const getCarouselQueries = (
			subjects: { id: string }[],
			classYears?: number[]
		): GetProductsQuery[] => {
			return subjects.map((subject) => ({
				subjects: [subject.id],
				...(classYears?.length ? { classYears } : {}),
			}));
		};
		const carouselQueries = getCarouselQueries(user.subjectsFlattened, user.classes);

		carouselQueries.forEach((query: GetProductsQuery) => {
			fetchProductsByQuery(query, CAROUSEL_DEFAULT_QUERY_OPTIONS);
		});
		// We are using subjects in the dependency array
		// since the reference to subjectsFlattened changes on every selector update
	}, [user?.subjects, user?.classes, enabled]);

	return { userSubjects, defaultSubject };
};

export default useFetchDashboardSubjects;
